import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { 
  DollarSign, 
  PiggyBank,
  TrendingUp,
  CreditCard,
  Target,
  LineChart,
  Wallet,
  ArrowRight,
  Plus,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import BetaModal from "@/components/invest/BetaModal";
import TransactionInsights from "@/components/finance/TransactionInsights";
import FinancialHealthOverview from "@/components/finance/FinancialHealthOverview";
import SmartFinancialAlerts from "@/components/finance/SmartFinancialAlerts";
import CentseScoreFinance from "@/components/finance/CentseScoreFinance";
import FinanceDashboardPreferences, { DashboardDetailLevel } from "@/components/finance/FinanceDashboardPreferences";
import BasicFinanceView from "@/components/finance/BasicFinanceView";

/**
 * Finance Dashboard - Focused on personal finance management and tracking
 * 
 * Features:
 * - Account balances and financial overview
 * - Spending insights and budget tracking
 * - Savings goals and investment portfolio
 * - Financial recommendations
 * - Quick access to finance tools
 */
const FinanceDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>("");
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [dashboardLevel, setDashboardLevel] = useState<DashboardDetailLevel>(() => {
    return (localStorage.getItem('finance-dashboard-level') as DashboardDetailLevel) || 'basic';
  });

  // Check for beta modal on component mount
  useEffect(() => {
    const hasSeenBetaModal = localStorage.getItem('invest-beta-modal-seen');
    if (!hasSeenBetaModal) {
      setShowBetaModal(true);
    }
  }, []);

  // Handle closing the beta modal
  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    localStorage.setItem('invest-beta-modal-seen', 'true');
  };

  // Handle dashboard level change
  const handleDashboardLevelChange = (level: DashboardDetailLevel) => {
    setDashboardLevel(level);
    localStorage.setItem('finance-dashboard-level', level);
  };

  // Fetch user profile to display personalized greeting
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', user.id)
            .single();
          
          if (profile?.username) {
            setUsername(profile.username);
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch user budgets for financial overview
  const { data: budgetsData } = useQuery({
    queryKey: ['userBudgets'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('budgets')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    }
  });

  // Fetch user points for financial goals tracking
  const { data: userPoints } = useQuery({
    queryKey: ['userPoints'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  // Calculate financial statistics
  const totalBudget = budgetsData?.reduce((sum, budget) => sum + budget.amount, 0) || 0;
  const totalSpent = budgetsData?.reduce((sum, budget) => sum + (budget.spent || 0), 0) || 0;
  const budgetUtilization = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
  const activeBudgets = budgetsData?.length || 0;
  const totalPoints = userPoints?.total_points || 0;

  // Mock data for demonstration (replace with real financial data)
  const mockFinancialData = {
    totalBalance: 2450.00,
    monthlyIncome: 1200.00,
    savingsGoal: 5000.00,
    currentSavings: 1850.00,
    investmentValue: 750.00
  };

  const savingsProgress = (mockFinancialData.currentSavings / mockFinancialData.savingsGoal) * 100;

  return (
    <>
      {/* Beta Modal for Finance Mode */}
      <BetaModal isOpen={showBetaModal} onClose={handleCloseBetaModal} />
      
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8 bg-background">
          
          {/* Header Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-secondary/10 rounded-lg">
                <DollarSign className="h-6 w-6 text-secondary" />
              </div>
              <Badge variant="secondary" className="bg-secondary/10 text-secondary">
                Finance Hub
              </Badge>
              <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">
                BETA
              </Badge>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900">
              Your financial overview{username ? `, ${username}` : ''} 💰
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              Track your spending, manage budgets, and grow your savings
            </p>
          </div>

          {/* Dashboard Preferences */}
          <FinanceDashboardPreferences 
            currentLevel={dashboardLevel}
            onLevelChange={handleDashboardLevelChange}
          />

          {/* Conditional Dashboard Content */}
          {dashboardLevel === 'basic' ? (
            <BasicFinanceView />
          ) : (
            <div className="space-y-8">
              {/* Finance-Focused Centse Score Card */}
              <CentseScoreFinance />

              {/* Smart Financial Alerts */}
              <SmartFinancialAlerts />

              {/* Financial Health Overview */}
              <FinancialHealthOverview />

              {/* Transaction Insights */}
              <TransactionInsights />

              {/* Financial Stats Overview - for detailed view only */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Monthly Budget</p>
                        <p className="text-3xl font-bold text-gray-900">${totalBudget.toFixed(0)}</p>
                        <p className="text-xs text-gray-500">{budgetUtilization.toFixed(0)}% used</p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-full">
                        <Wallet className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Active Budgets</p>
                        <p className="text-3xl font-bold text-gray-900">{activeBudgets}</p>
                        <p className="text-xs text-gray-500">categories tracked</p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-full">
                        <Target className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Savings Goal</p>
                        <p className="text-3xl font-bold text-gray-900">${mockFinancialData.currentSavings.toFixed(0)}</p>
                        <p className="text-xs text-gray-500">of ${mockFinancialData.savingsGoal.toFixed(0)}</p>
                      </div>
                      <div className="p-3 bg-yellow-100 rounded-full">
                        <PiggyBank className="h-6 w-6 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Investments</p>
                        <p className="text-3xl font-bold text-gray-900">${mockFinancialData.investmentValue.toFixed(0)}</p>
                        <p className="text-xs text-green-600">+8.5% this month</p>
                      </div>
                      <div className="p-3 bg-purple-100 rounded-full">
                        <TrendingUp className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Savings Progress */}
              <Card className="bg-background border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <PiggyBank className="h-5 w-5" />
                      Savings Goal Progress
                    </CardTitle>
                    <Badge variant="secondary" className="bg-secondary/10 text-secondary">
                      {savingsProgress.toFixed(0)}% Complete
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>${mockFinancialData.currentSavings.toFixed(2)} saved</span>
                      <span>${mockFinancialData.savingsGoal.toFixed(2)} goal</span>
                    </div>
                    <Progress value={savingsProgress} className="h-3" />
                    <p className="text-xs text-gray-500 text-center">
                      ${(mockFinancialData.savingsGoal - mockFinancialData.currentSavings).toFixed(2)} remaining to reach your goal
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Budget Overview */}
              {budgetsData && budgetsData.length > 0 && (
                <Card className="bg-background border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Budget Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {budgetsData.slice(0, 3).map((budget, index) => {
                        const utilization = budget.amount > 0 ? ((budget.spent || 0) / budget.amount) * 100 : 0;
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-gray-900 capitalize">{budget.category}</p>
                                <p className="text-sm text-gray-500">{budget.period || 'monthly'} budget</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium">${(budget.spent || 0).toFixed(0)} / ${budget.amount.toFixed(0)}</p>
                                <p className="text-xs text-gray-500">{utilization.toFixed(0)}% used</p>
                              </div>
                            </div>
                            <Progress 
                              value={utilization} 
                              className={`h-2 ${utilization > 90 ? 'bg-red-100' : utilization > 75 ? 'bg-yellow-100' : 'bg-green-100'}`} 
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Finance Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card 
                  className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  onClick={() => navigate("/save")}
                >
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-secondary/10 rounded-lg w-fit group-hover:scale-110 transition-transform">
                        <PiggyBank className="h-6 w-6 text-secondary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Manage Savings</h3>
                        <p className="text-sm text-gray-600">Set goals and track your savings progress</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  onClick={() => navigate("/invest")}
                >
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-green-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Invest & Grow</h3>
                        <p className="text-sm text-gray-600">Build your investment portfolio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  onClick={() => navigate("/track")}
                >
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-blue-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                        <LineChart className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Track Expenses</h3>
                        <p className="text-sm text-gray-600">Monitor spending and budget usage</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Recommendations */}
              <Card className="bg-gradient-to-r from-secondary/5 to-secondary/10 border-secondary/20">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-secondary" />
                    Smart Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-background rounded-lg border border-secondary/20">
                      <div className="p-2 bg-secondary/10 rounded-lg">
                        <PiggyBank className="h-4 w-4 text-secondary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Increase Emergency Fund</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Consider saving an additional $200/month to reach 6 months of expenses
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-background rounded-lg border border-secondary/20">
                      <div className="p-2 bg-secondary/10 rounded-lg">
                        <TrendingUp className="h-4 w-4 text-secondary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Investment Opportunity</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          You have $300 available to invest based on your current spending patterns
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FinanceDashboard;